import { Box, Center, Flex, FormControl, FormLabel, Spinner, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { $email, $ok, $UUID } from "foundation-ts/commons";
import { UUID } from "foundation-ts/types";
import { CorporationDto } from "doxecure-commons/lib/doxecureClientTypes";
import { corporationIdentifiersDictionary } from "doxecure-commons/lib/doxecureLocales";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { ErrorField } from "@components/ErrorField" ;
import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { SeaShellBox } from "@components/SeaShellBox" ;
import { UserInfosProperties, UserParameters } from "@components/UserInfos/UserInfos";
import { useDevice } from "@hooks/useDevice";
import { getCorporation } from "@services/corporations.service";
import { $g1Color } from "@/theme/G1Style";
import { userRoleAndStatus } from "@utils/functions";
import { G1Icon } from "@utils/icons";

type CorporationUserInfosValues = {
    firstName: string,
    lastName: string,
    email: string,
    apid: string,
    corporationIdentifier: string
} ;

export const CorporationUserInfos = (props: UserInfosProperties) => {
    const { data, actionButtons, maxWidth, visibility, isAdminCorporate, isCreation, onBack } = props ;
    const { isMOBILE } = useDevice() ;
    const [corporation, setCorporation] = useState<CorporationDto|null>(null) ;
    const [isLoadingCorporation, setIsLoadingCorporation] = useState(false);
    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<any>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            lastName: "",
            firstName: "",
            email: "",
            apid: null,
        }
    });

    const mailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    const isDisabledUserInfos = $ok(data?.apid);

    const onSubmit: SubmitHandler<any> = useCallback((formData: CorporationUserInfosValues, event) => {
        event?.preventDefault();
        
        const dataUser: UserParameters = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: $email(formData.email)!,
            corporationId: $UUID(corporation?.apid)!,
        };
        
        if ($ok(formData.apid)) {
            dataUser.apid = $UUID(formData.apid)!;
        }

        const button = (event?.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const buttonIndex = button.getAttribute('data-index');
        if ($ok(buttonIndex)) {
            actionButtons[Number(buttonIndex)].onSubmit(dataUser);
        }
    }, [corporation?.apid, actionButtons]);

    const CorporationInfosFields = () => {
        if (isAdminCorporate) { return null; }

        if (isLoadingCorporation) { return <Spinner />; }

        return (
            <>
                <FormControl isDisabled={isDisabledUserInfos || isCreation}>
                    <FormLabel fontSize="1.25em">Organisation</FormLabel>
                    <G1Input component="form" textTransform="uppercase" value={corporation?.name || ''} isReadOnly />
                </FormControl>
                
                <FormControl isDisabled={isDisabledUserInfos || isCreation} marginTop={3}>
                    <FormLabel fontSize="1.25em">
                        {`Identifiant "${$ok(corporation?.corporationIdentifierType) 
                            ? corporationIdentifiersDictionary[corporation!.corporationIdentifierType]?.name 
                            : ''}"`}
                    </FormLabel>
                    <G1Input 
                        component="form" 
                        textTransform="uppercase" 
                        value={corporation?.corporationIdentifier || ''} 
                        isReadOnly
                    />
                </FormControl>
            </>
        );
    };

    useEffect(() => {
        let isCancelled = false;
        const getCorporationInfos = async (corporationId: UUID) => {
            setIsLoadingCorporation(true);
            try {
                const corporationData = await getCorporation(corporationId);
                if (!isCancelled) {
                    setCorporation(corporationData);
                }
            } catch (error) {
                console.error('Erreur lors du chargement de la corporation:', error);
            } finally {
                if (!isCancelled) {
                    setIsLoadingCorporation(false);
                }
            }
        };

        if ($ok(data?.corporationId)) {
            getCorporationInfos(data!.corporationId!);
        } else {
            setCorporation(null);
        }

        return () => { isCancelled = true; };
    }, [data?.corporationId]); 

    useEffect(() => {
        if ($ok(data)) {
            reset({
                lastName: data!.lastName || "",
                firstName: data!.firstName || "",
                email: $ok(data!.email) ? data!.email.toString() : "",
                apid: data!.apid || null,
            });
        }
    }, [data]);

    return (
        <>
            <ColumnTitle display={isMOBILE ? "block" : "none"} color={$g1Color('global.write')}>
                <Flex alignItems="center" as="button" onClick={onBack} width="100%">
                    <Center color={$g1Color('global.icons.navigation.previous.write')}>
                        <G1Icon.Back />
                    </Center>
                    <Text>Retour</Text>
                </Flex>
            </ColumnTitle>
            
            {!isAdminCorporate && isCreation && 
                <ColumnTitle color={$g1Color('global.write')}>
                    <Text>Création d'un gestionnaire</Text>
                </ColumnTitle>
            }
            
            <Box visibility={visibility} width="100%" mt={10} maxW={maxWidth}>
                <SeaShellBox component="form">
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <CorporationInfosFields />

                        <FormControl isRequired isDisabled={isDisabledUserInfos} marginTop={3}>
                            <FormLabel fontSize="1.25em">Nom</FormLabel>
                            <G1Input component="form" registerInput={{ ...register("lastName", { required: true }) }} textTransform="uppercase" />
                            {errors.lastName && <ErrorField fieldName="nom" />}
                        </FormControl>

                        <FormControl isRequired isDisabled={isDisabledUserInfos} marginTop={3}>
                            <FormLabel fontSize="1.25em">Prénom</FormLabel>
                            <G1Input component="form" registerInput={{ ...register("firstName", { required: true }) }} textTransform="capitalize" />
                            {errors.firstName && <ErrorField fieldName="prénom" />}
                        </FormControl>

                        <FormControl isRequired isDisabled={isDisabledUserInfos} marginTop={3}>
                            <FormLabel fontSize="1.25em">Adresse email</FormLabel>
                            <G1Input 
                                component="form" 
                                type="email" 
                                registerInput={{ ...register("email", { 
                                    required: "L'email est requis", 
                                    pattern: {
                                        value: mailPattern,
                                        message: "Format d'email invalide"
                                    }
                                })}} 
                                textTransform="lowercase" 
                                autoComplete="email" 
                            />
                            {errors.email && (
                                <ErrorField fieldName="email" error={errors.email.message as (string|undefined) || "Le champ email est requis ou est invalide"} />
                            )}
                        </FormControl>

                        {!isCreation && (
                            <Box marginTop="20px">
                                <Text fontSize="md" fontWeight="bold" display="inline-block">
                                    Profil:
                                </Text>
                                <Text fontSize="md" display="inline-block" marginLeft="5px">
                                    {userRoleAndStatus(data?.userFlags!, isAdminCorporate)}
                                </Text>
                            </Box>
                        )}

                        <input type="hidden" {...register("apid")} />

                        {actionButtons.length > 0 && (
                            <Flex direction="row" justifyContent="center" marginTop="10px">
                                {actionButtons.map((button, index) => (
                                    <G1Button 
                                        key={index} 
                                        component="form" 
                                        variant={button.variant} 
                                        type="submit" 
                                        title={button.title} 
                                        marginRight="2" 
                                        fontSize="md" 
                                        data-index={index} 
                                    />
                                ))}
                            </Flex>
                        )}
                    </form>
                </SeaShellBox>
            </Box>
        </>
    );
} ;